// Enhanced company data service that aggregates data from multiple sources
export interface StockData {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  marketCap: number;
  volume: number;
  lastUpdated: string;
}

export interface CrunchbaseData {
  foundingDate: string;
  totalFunding: number;
  lastFundingDate: string;
  lastFundingAmount: number;
  investors: string[];
  acquisitions: string[];
  subsidiaries: string[];
  keyPeople: {
    name: string;
    role: string;
    image?: string;
  }[];
}

export interface CompanyNews {
  title: string;
  summary: string;
  url: string;
  publishedAt: string;
  source: string;
}

export interface JobListing {
  id: string;
  title: string;
  department: string;
  location: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Remote' | 'Hybrid';
  salaryMin: number;
  salaryMax: number;
  description: string;
  postedDate: string;
  requirements: string[];
  experienceLevel: 'Entry Level' | 'Mid Level' | 'Senior Level' | 'Executive';
  benefits: string[];
  skills: string[];
  remote: boolean;
  urgentHiring: boolean;
}

export interface CompanyReview {
  title: string;
  rating: number;
  role: string;
  date: string;
  currentEmployee: boolean;
  pros: string;
  cons: string;
}

export interface CompanyBenefit {
  name: string;
  icon: string;
  category: 'health' | 'financial' | 'lifestyle' | 'professional';
}

export interface CompanyCompetitor {
  name: string;
  rating: number;
  avgSalary: number;
  openJobs: number;
}

export interface CompanyDetailedInfo {
  id: string;
  description: string;
  headquarters: string;
  ceo: string;
  website: string;
  stockSymbol?: string;
  reviewCount: number;
  recommendToFriend: number;
  ceoApproval: number;
  industryRankPercentile: number;
  salaryVsMarket: number;
  avgSalary?: number;
  growthRate?: number;
  employeeCount?: number;
  hasLayoffs?: boolean;
  layoffPercentage?: number;
  metrics?: {
    workLifeBalance: number;
    careerOpportunities: number;
    compensationBenefits: number;
    seniorManagement: number;
    cultureValues: number;
  };
  topSkills: string[];
  benefits: CompanyBenefit[];
  jobListings: JobListing[];
  reviews: CompanyReview[];
  competitors: CompanyCompetitor[];
  
  // Real-time financial data
  stockData?: StockData;
  
  // Crunchbase data
  crunchbaseData?: CrunchbaseData;
  
  // Recent news
  recentNews: CompanyNews[];
  
  // Enhanced glassdoor data
  glassdoorData?: {
    overallRating: number;
    cultureRating: number;
    diversityRating: number;
    workLifeBalanceRating: number;
    seniorManagementRating: number;
    compensationRating: number;
    careerOpportunitiesRating: number;
    recommendToFriendPct: number;
    ceoApprovalPct: number;
    businessOutlook: 'positive' | 'neutral' | 'negative';
    totalReviews: number;
    salaryTransparency: number;
  };
  
  // Enhanced levels.fyi data
  levelsData?: {
    averageTotalCompensation: number;
    medianTotalCompensation: number;
    salaryByLevel: {
      level: string;
      title: string;
      baseSalary: number;
      totalCompensation: number;
      stockValue: number;
      bonus: number;
    }[];
    compensationTrends: {
      year: number;
      averageCompensation: number;
    }[];
  };
  
  // Job market data
  jobMarketData: {
    totalOpenings: number;
    hiringTrend: 'increasing' | 'stable' | 'decreasing';
    averageTimeToHire: number;
    mostInDemandSkills: string[];
    departmentGrowth: {
      department: string;
      growthRate: number;
      openings: number;
    }[];
  };
}

class CompanyDataService {
  private baseUrl: string;
  private alphaVantageApiKey: string;
  private crunchbaseApiKey: string;
  private newsApiKey: string;
  
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    this.alphaVantageApiKey = process.env.REACT_APP_ALPHA_VANTAGE_API_KEY || 'demo';
    this.crunchbaseApiKey = process.env.REACT_APP_CRUNCHBASE_API_KEY || '';
    this.newsApiKey = process.env.REACT_APP_NEWS_API_KEY || '';
  }
  
  async getCompanyDetailedInfo(companyId: string): Promise<CompanyDetailedInfo> {
    try {
      // Get basic company info from our backend
      const basicInfo = await this.fetchBasicInfo(companyId);
      
      // Fetch data from multiple sources in parallel
      const [glassdoorData, levelsData, stockData, crunchbaseData, newsData] = await Promise.all([
        this.fetchGlassdoorData(companyId),
        this.fetchLevelsData(companyId),
        this.fetchStockData(basicInfo.stockSymbol),
        this.fetchCrunchbaseData(companyId),
        this.fetchCompanyNews(basicInfo.name)
      ]);
      
      return this.aggregateCompanyData(basicInfo, glassdoorData, levelsData, stockData, crunchbaseData, newsData);
    } catch (error) {
      console.error('Error fetching company detailed info:', error);
      // Return enhanced mock data for demonstration
      return this.getMockCompanyDetails(companyId);
    }
  }
  
  private async fetchBasicInfo(companyId: string): Promise<any> {
    // Skip API call if using mock data
    if (process.env.REACT_APP_USE_MOCK_DATA === 'true') {
      return this.getMockCompanyInfo(companyId);
    }
    
    try {
      const response = await fetch(`${this.baseUrl}/api/companies/${companyId}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.warn('API unavailable, using mock company data');
      // Return mock company data when API is unavailable
      return this.getMockCompanyInfo(companyId);
    }
    return this.getMockCompanyInfo(companyId);
  }
  
  private getMockCompanyInfo(companyId: string): any {
    const mockCompanies: { [key: string]: any } = {
      '5': {
        id: '5',
        name: 'Tech Solutions Inc.',
        stockSymbol: 'TECH',
        description: 'Leading technology solutions provider',
        founded: 2010,
        employees: 5000,
        headquarters: 'San Francisco, CA',
        website: 'https://techsolutions.example.com'
      },
      '38': {
        id: '38',
        name: 'Global Consulting Group',
        stockSymbol: 'GCG',
        description: 'International business consulting firm',
        founded: 1995,
        employees: 15000,
        headquarters: 'New York, NY',
        website: 'https://globalconsulting.example.com'
      }
    };
    
    return mockCompanies[companyId] || {
      id: companyId,
      name: `Company ${companyId}`,
      stockSymbol: null,
      description: 'A great company to work for',
      founded: 2000,
      employees: 1000,
      headquarters: 'United States',
      website: 'https://example.com'
    };
  }
  
  private async fetchGlassdoorData(companyId: string): Promise<any> {
    // Simulate Glassdoor API call - in production, use official Glassdoor API
    return new Promise(resolve => {
      setTimeout(() => resolve({}), 100);
    });
  }
  
  private async fetchLevelsData(companyId: string): Promise<any> {
    // Simulate Levels.fyi API call - in production, use their API or scraping
    return new Promise(resolve => {
      setTimeout(() => resolve({}), 100);
    });
  }
  
  private async fetchStockData(stockSymbol: string | null): Promise<StockData | null> {
    if (!stockSymbol || this.alphaVantageApiKey === 'demo') {
      return null;
    }
    
    try {
      // Using Alpha Vantage API for real-time stock data
      const response = await fetch(
        `https://www.alphavantage.co/query?function=GLOBAL_QUOTE&symbol=${stockSymbol}&apikey=${this.alphaVantageApiKey}`
      );
      
      if (response.ok) {
        const data = await response.json();
        const quote = data['Global Quote'];
        
        if (quote) {
          return {
            symbol: stockSymbol,
            price: parseFloat(quote['05. price']),
            change: parseFloat(quote['09. change']),
            changePercent: parseFloat(quote['10. change percent'].replace('%', '')),
            marketCap: 0, // Would need additional API call
            volume: parseInt(quote['06. volume']),
            lastUpdated: quote['07. latest trading day']
          };
        }
      }
    } catch (error) {
      console.error('Error fetching stock data:', error);
    }
    
    return null;
  }
  
  private async fetchCrunchbaseData(companyId: string): Promise<CrunchbaseData | null> {
    if (!this.crunchbaseApiKey) {
      return null;
    }
    
    try {
      // Simulate Crunchbase API call - in production, use official Crunchbase API
      // Note: Crunchbase API requires paid subscription for most endpoints
      return null;
    } catch (error) {
      console.error('Error fetching Crunchbase data:', error);
      return null;
    }
  }
  
  private async fetchCompanyNews(companyName: string): Promise<CompanyNews[]> {
    if (!this.newsApiKey) {
      return [];
    }
    
    try {
      // Using NewsAPI for company-related news
      const response = await fetch(
        `https://newsapi.org/v2/everything?q="${companyName}"&sortBy=publishedAt&language=en&pageSize=5&apiKey=${this.newsApiKey}`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        return data.articles.map((article: any) => ({
          title: article.title,
          summary: article.description,
          url: article.url,
          publishedAt: article.publishedAt,
          source: article.source.name
        }));
      }
    } catch (error) {
      console.error('Error fetching company news:', error);
    }
    
    return [];
  }
  
  private aggregateCompanyData(basic: any, glassdoor: any, levels: any, stockData?: StockData | null, crunchbaseData?: CrunchbaseData | null, newsData?: CompanyNews[]): CompanyDetailedInfo {
    // In production, this would merge real data from multiple sources
    const mockData = this.getMockCompanyDetails(basic.id || 'default');
    
    // Add real-time data if available
    if (stockData) {
      mockData.stockData = stockData;
    }
    
    if (crunchbaseData) {
      mockData.crunchbaseData = crunchbaseData;
    }
    
    if (newsData && newsData.length > 0) {
      mockData.recentNews = newsData;
    }
    
    return mockData;
  }
  
  private getMockCompanyDetails(companyId: string): CompanyDetailedInfo {
    const mockCompanies: { [key: string]: CompanyDetailedInfo } = {
      'basf': {
        id: 'basf',
        description: 'BASF SE is a German multinational chemical company and the largest chemical producer in the world. The company is headquartered in Ludwigshafen, Germany, and operates in more than 80 countries. BASF creates chemistry for a sustainable future by combining economic success with environmental protection and social responsibility.',
        headquarters: 'Ludwigshafen, Germany',
        ceo: 'Martin Brudermüller',
        website: 'https://www.basf.com',
        stockSymbol: 'BAS.DE',
        reviewCount: 3247,
        recommendToFriend: 72,
        ceoApproval: 78,
        industryRankPercentile: 82,
        salaryVsMarket: 8,
        topSkills: ['Chemical Engineering', 'Process Engineering', 'R&D', 'Sustainability', 'SAP', 'Project Management'],
        benefits: [
          { name: 'Health Insurance', icon: 'local_hospital', category: 'health' },
          { name: 'Dental Insurance', icon: 'medical_services', category: 'health' },
          { name: 'Vision Insurance', icon: 'visibility', category: 'health' },
          { name: 'Company Pension', icon: 'savings', category: 'financial' },
          { name: 'Employee Stock Purchase', icon: 'trending_up', category: 'financial' },
          { name: 'Flexible Work Hours', icon: 'schedule', category: 'lifestyle' },
          { name: 'Home Office', icon: 'home', category: 'lifestyle' },
          { name: 'Gym & Wellness', icon: 'fitness_center', category: 'lifestyle' },
          { name: 'Professional Development', icon: 'school', category: 'professional' },
          { name: 'Conference Budget', icon: 'event', category: 'professional' },
          { name: 'Parental Leave', icon: 'child_care', category: 'lifestyle' },
          { name: 'Sabbatical Program', icon: 'travel_explore', category: 'lifestyle' }
        ],
        jobListings: [
          {
            id: 'basf1',
            title: 'Senior Process Engineer - Catalysts',
            department: 'Engineering',
            location: 'Ludwigshafen, Germany',
            type: 'Full-time',
            salaryMin: 75000,
            salaryMax: 95000,
            description: 'Lead process optimization projects for catalyst production, focusing on sustainability and efficiency improvements.',
            postedDate: '2024-01-12',
            requirements: ['Chemical Engineering degree', '5+ years process engineering', 'Catalyst development experience'],
            experienceLevel: 'Senior Level',
            benefits: ['Company car', 'Relocation assistance', 'Professional development budget'],
            skills: ['Process Engineering', 'Chemical Catalysis', 'Process Simulation', 'Six Sigma'],
            remote: false,
            urgentHiring: false
          },
          {
            id: 'basf2',
            title: 'Sustainability Manager - Circular Economy',
            department: 'Sustainability',
            location: 'Berlin, Germany',
            type: 'Full-time',
            salaryMin: 85000,
            salaryMax: 110000,
            description: 'Drive circular economy initiatives across BASF operations, develop sustainability metrics and reporting.',
            postedDate: '2024-01-10',
            requirements: ['Environmental Science/Engineering degree', 'Sustainability experience', 'Project management skills'],
            experienceLevel: 'Senior Level',
            benefits: ['Hybrid work', 'Sustainability training', 'Industry conference access'],
            skills: ['Sustainability', 'Circular Economy', 'Life Cycle Assessment', 'ESG Reporting'],
            remote: true,
            urgentHiring: true
          },
          {
            id: 'basf3',
            title: 'Digital Transformation Specialist',
            department: 'IT',
            location: 'Munich, Germany',
            type: 'Full-time',
            salaryMin: 70000,
            salaryMax: 90000,
            description: 'Lead digital transformation projects, implement Industry 4.0 solutions in chemical manufacturing.',
            postedDate: '2024-01-08',
            requirements: ['Computer Science/Engineering degree', 'Digital transformation experience', 'Manufacturing knowledge'],
            experienceLevel: 'Mid Level',
            benefits: ['Latest tech equipment', 'Training budget', 'Flexible hours'],
            skills: ['Digital Transformation', 'Industry 4.0', 'IoT', 'Data Analytics', 'SAP'],
            remote: false,
            urgentHiring: false
          }
        ],
        reviews: [
          {
            title: 'Great company for chemical engineers',
            rating: 4.3,
            role: 'Process Engineer',
            date: '2024-01-15',
            currentEmployee: true,
            pros: 'Excellent R&D facilities, strong focus on sustainability, good work-life balance, comprehensive benefits',
            cons: 'Large bureaucracy can slow decision making, limited remote work options in some departments'
          },
          {
            title: 'Innovation leader in chemicals',
            rating: 4.1,
            role: 'Research Scientist',
            date: '2023-12-20',
            currentEmployee: true,
            pros: 'Cutting-edge research projects, collaborative environment, excellent training programs, job security',
            cons: 'Hierarchical structure, slow pace of change, limited startup-like agility'
          },
          {
            title: 'Solid career development',
            rating: 4.0,
            role: 'Project Manager',
            date: '2023-11-30',
            currentEmployee: false,
            pros: 'Strong mentorship, global opportunities, good compensation, stable company',
            cons: 'Conservative culture, limited digital innovation, complex approval processes'
          }
        ],
        competitors: [
          { name: 'BASF', rating: 4.2, avgSalary: 82000, openJobs: 190 },
          { name: 'Dow Chemical', rating: 4.0, avgSalary: 85000, openJobs: 156 },
          { name: 'DuPont', rating: 4.1, avgSalary: 88000, openJobs: 134 },
          { name: 'Bayer', rating: 3.9, avgSalary: 79000, openJobs: 201 },
          { name: 'Evonik', rating: 4.0, avgSalary: 76000, openJobs: 89 }
        ],
        recentNews: [
          {
            title: 'BASF announces €2 billion investment in battery materials',
            summary: 'Major expansion of battery materials production to support electric vehicle market growth.',
            url: 'https://www.basf.com/news/battery-investment',
            publishedAt: '2024-01-10T10:00:00Z',
            source: 'BASF News'
          },
          {
            title: 'BASF achieves carbon neutrality milestone in European operations',
            summary: 'Company reaches significant sustainability target ahead of schedule.',
            url: 'https://www.basf.com/news/carbon-neutral',
            publishedAt: '2024-01-08T14:30:00Z',
            source: 'Chemical Week'
          }
        ],
        glassdoorData: {
          overallRating: 4.2,
          cultureRating: 4.0,
          diversityRating: 4.1,
          workLifeBalanceRating: 4.3,
          seniorManagementRating: 3.8,
          compensationRating: 4.0,
          careerOpportunitiesRating: 4.2,
          recommendToFriendPct: 72,
          ceoApprovalPct: 78,
          businessOutlook: 'positive',
          totalReviews: 3247,
          salaryTransparency: 3.9
        },
        levelsData: {
          averageTotalCompensation: 95000,
          medianTotalCompensation: 88000,
          salaryByLevel: [
            { level: 'E3', title: 'Engineer', baseSalary: 65000, totalCompensation: 72000, stockValue: 5000, bonus: 2000 },
            { level: 'E4', title: 'Senior Engineer', baseSalary: 80000, totalCompensation: 90000, stockValue: 7000, bonus: 3000 },
            { level: 'E5', title: 'Principal Engineer', baseSalary: 100000, totalCompensation: 115000, stockValue: 10000, bonus: 5000 },
            { level: 'E6', title: 'Staff Engineer', baseSalary: 120000, totalCompensation: 140000, stockValue: 15000, bonus: 5000 }
          ],
          compensationTrends: [
            { year: 2021, averageCompensation: 85000 },
            { year: 2022, averageCompensation: 89000 },
            { year: 2023, averageCompensation: 95000 }
          ]
        },
        avgSalary: 82000,
        growthRate: 5.2,
        employeeCount: 110000,
        hasLayoffs: false,
        metrics: {
          workLifeBalance: 4.3,
          careerOpportunities: 4.2,
          compensationBenefits: 4.0,
          seniorManagement: 3.8,
          cultureValues: 4.0
        },
        jobMarketData: {
          totalOpenings: 190,
          hiringTrend: 'increasing',
          averageTimeToHire: 42,
          mostInDemandSkills: ['Chemical Engineering', 'Sustainability', 'Process Optimization', 'Digital Manufacturing'],
          departmentGrowth: [
            { department: 'Engineering', growthRate: 12, openings: 78 },
            { department: 'Sustainability', growthRate: 25, openings: 34 },
            { department: 'R&D', growthRate: 8, openings: 45 },
            { department: 'IT', growthRate: 18, openings: 33 }
          ]
        }
      },
      'spotify': {
        id: 'spotify',
        description: 'Spotify is a digital music service that gives you access to millions of songs. We believe music is universal and should be accessible to everyone. Our mission is to unlock the potential of human creativity by giving a million creative artists the opportunity to live off their art and billions of fans the opportunity to enjoy and be inspired by it.',
        headquarters: 'Stockholm, Sweden',
        ceo: 'Daniel Ek',
        website: 'https://www.spotify.com',
        stockSymbol: 'SPOT',
        reviewCount: 2847,
        recommendToFriend: 78,
        ceoApproval: 82,
        industryRankPercentile: 85,
        salaryVsMarket: 15,
        topSkills: ['Python', 'React', 'Java', 'AWS', 'Machine Learning', 'Kubernetes'],
        benefits: [
          { name: 'Health Insurance', icon: 'local_hospital', category: 'health' },
          { name: 'Dental Insurance', icon: 'medical_services', category: 'health' },
          { name: 'Vision Insurance', icon: 'visibility', category: 'health' },
          { name: 'Mental Health Support', icon: 'psychology', category: 'health' },
          { name: '401(k) Matching', icon: 'account_balance', category: 'financial' },
          { name: 'Stock Options', icon: 'trending_up', category: 'financial' },
          { name: 'Flexible Work Hours', icon: 'schedule', category: 'lifestyle' },
          { name: 'Remote Work Options', icon: 'home', category: 'lifestyle' },
          { name: 'Gym Membership', icon: 'fitness_center', category: 'lifestyle' },
          { name: 'Learning Budget', icon: 'school', category: 'professional' },
          { name: 'Conference Attendance', icon: 'event', category: 'professional' },
          { name: 'Parental Leave', icon: 'child_care', category: 'lifestyle' }
        ],
        jobListings: [
          {
            id: 'job1',
            title: 'Senior Backend Engineer',
            department: 'Engineering',
            location: 'Stockholm, Sweden',
            type: 'Full-time',
            salaryMin: 85000,
            salaryMax: 120000,
            description: 'Join our platform team to build scalable services that power music streaming for millions of users worldwide.',
            postedDate: '2024-01-10',
            requirements: ['5+ years backend experience', 'Java/Python expertise', 'Distributed systems knowledge'],
            experienceLevel: 'Senior Level',
            benefits: ['Health insurance', 'Stock options', 'Flexible hours'],
            skills: ['Java', 'Python', 'Microservices', 'AWS'],
            remote: false,
            urgentHiring: false
          },
          {
            id: 'job2',
            title: 'Product Manager - Discovery',
            department: 'Product',
            location: 'New York, NY',
            type: 'Full-time',
            salaryMin: 110000,
            salaryMax: 150000,
            description: 'Lead product strategy for music discovery features, working closely with ML engineers and designers.',
            postedDate: '2024-01-08',
            requirements: ['3+ years PM experience', 'Data-driven decision making', 'Consumer product background'],
            experienceLevel: 'Mid Level',
            benefits: ['Health insurance', 'Stock options', 'Remote work'],
            skills: ['Product Strategy', 'Data Analysis', 'A/B Testing', 'User Research'],
            remote: false,
            urgentHiring: true
          },
          {
            id: 'job3',
            title: 'Machine Learning Engineer',
            department: 'Engineering',
            location: 'Remote',
            type: 'Remote',
            salaryMin: 95000,
            salaryMax: 140000,
            description: 'Develop recommendation algorithms that help users discover their next favorite artists.',
            postedDate: '2024-01-05',
            requirements: ['ML/AI expertise', 'Python/TensorFlow', 'Experience with recommendation systems'],
            experienceLevel: 'Senior Level',
            benefits: ['Remote work', 'Health insurance', 'Learning budget'],
            skills: ['Machine Learning', 'Python', 'TensorFlow', 'Recommendation Systems'],
            remote: true,
            urgentHiring: false
          }
        ],
        reviews: [
          {
            title: 'Great culture, innovative environment',
            rating: 4.5,
            role: 'Software Engineer',
            date: '2024-01-15',
            currentEmployee: true,
            pros: 'Amazing work culture, flexible hours, great benefits, cutting-edge technology, collaborative environment',
            cons: 'Fast-paced environment can be stressful, some teams are better than others'
          },
          {
            title: 'Good place to grow your career',
            rating: 4.0,
            role: 'Product Manager',
            date: '2023-12-20',
            currentEmployee: true,
            pros: 'Innovative products, smart colleagues, good work-life balance, remote work options',
            cons: 'Reorganizations can be disruptive, competition for resources between teams'
          },
          {
            title: 'Challenging but rewarding',
            rating: 4.2,
            role: 'Data Scientist',
            date: '2023-11-30',
            currentEmployee: false,
            pros: 'Interesting problems to solve, access to vast amounts of data, supportive team',
            cons: 'High expectations, occasional long hours during product launches'
          }
        ],
        competitors: [
          { name: 'Spotify', rating: 4.1, avgSalary: 95000, openJobs: 234 },
          { name: 'Apple Music', rating: 4.2, avgSalary: 115000, openJobs: 456 },
          { name: 'YouTube Music', rating: 4.3, avgSalary: 125000, openJobs: 312 },
          { name: 'SoundCloud', rating: 3.8, avgSalary: 75000, openJobs: 87 },
          { name: 'Tidal', rating: 3.6, avgSalary: 70000, openJobs: 45 }
        ],
        recentNews: [
          {
            title: 'Spotify reports strong Q4 growth in premium subscribers',
            summary: 'Music streaming giant adds 10 million new premium subscribers in the quarter.',
            url: 'https://newsroom.spotify.com/q4-results',
            publishedAt: '2024-01-12T16:00:00Z',
            source: 'Spotify Newsroom'
          }
        ],
        glassdoorData: {
          overallRating: 4.1,
          cultureRating: 4.3,
          diversityRating: 4.0,
          workLifeBalanceRating: 4.2,
          seniorManagementRating: 3.8,
          compensationRating: 4.0,
          careerOpportunitiesRating: 4.1,
          recommendToFriendPct: 78,
          ceoApprovalPct: 82,
          businessOutlook: 'positive',
          totalReviews: 2847,
          salaryTransparency: 4.2
        },
        levelsData: {
          averageTotalCompensation: 145000,
          medianTotalCompensation: 130000,
          salaryByLevel: [
            { level: 'L3', title: 'Software Engineer', baseSalary: 95000, totalCompensation: 115000, stockValue: 15000, bonus: 5000 },
            { level: 'L4', title: 'Senior Software Engineer', baseSalary: 120000, totalCompensation: 150000, stockValue: 25000, bonus: 5000 },
            { level: 'L5', title: 'Staff Software Engineer', baseSalary: 150000, totalCompensation: 200000, stockValue: 40000, bonus: 10000 },
            { level: 'L6', title: 'Principal Engineer', baseSalary: 180000, totalCompensation: 250000, stockValue: 60000, bonus: 10000 }
          ],
          compensationTrends: [
            { year: 2021, averageCompensation: 125000 },
            { year: 2022, averageCompensation: 138000 },
            { year: 2023, averageCompensation: 145000 }
          ]
        },
        avgSalary: 145000,
        growthRate: 12.3,
        employeeCount: 9800,
        hasLayoffs: true,
        layoffPercentage: 17,
        metrics: {
          workLifeBalance: 3.8,
          careerOpportunities: 4.4,
          compensationBenefits: 4.5,
          seniorManagement: 4.0,
          cultureValues: 4.3
        },
        jobMarketData: {
          totalOpenings: 234,
          hiringTrend: 'increasing',
          averageTimeToHire: 28,
          mostInDemandSkills: ['Python', 'React', 'Machine Learning', 'Data Engineering'],
          departmentGrowth: [
            { department: 'Engineering', growthRate: 15, openings: 156 },
            { department: 'Product', growthRate: 8, openings: 45 },
            { department: 'Data Science', growthRate: 22, openings: 33 }
          ]
        }
      },
      'default': {
        id: 'default',
        description: 'This company is a leader in its industry, known for innovation and employee satisfaction. With a strong focus on technology and customer experience, it continues to grow and expand its market presence.',
        headquarters: 'San Francisco, CA',
        ceo: 'John Smith',
        website: 'https://example.com',
        stockSymbol: 'EXMP',
        reviewCount: 1234,
        recommendToFriend: 75,
        ceoApproval: 80,
        industryRankPercentile: 75,
        salaryVsMarket: 10,
        topSkills: ['JavaScript', 'React', 'Node.js', 'AWS', 'Docker', 'Python'],
        benefits: [
          { name: 'Health Insurance', icon: 'local_hospital', category: 'health' },
          { name: 'Dental Insurance', icon: 'medical_services', category: 'health' },
          { name: '401(k) Matching', icon: 'account_balance', category: 'financial' },
          { name: 'Stock Options', icon: 'trending_up', category: 'financial' },
          { name: 'Flexible Work Hours', icon: 'schedule', category: 'lifestyle' },
          { name: 'Remote Work', icon: 'home', category: 'lifestyle' },
          { name: 'Professional Development', icon: 'school', category: 'professional' }
        ],
        jobListings: [
          {
            id: 'job1',
            title: 'Senior Frontend Engineer',
            department: 'Engineering',
            location: 'San Francisco, CA',
            type: 'Full-time',
            salaryMin: 120000,
            salaryMax: 160000,
            description: 'Build beautiful and performant user interfaces for our products.',
            postedDate: '2024-01-12',
            requirements: ['5+ years frontend experience', 'React expertise', 'Strong design sense'],
            experienceLevel: 'Senior Level',
            benefits: ['Health insurance', 'Stock options', 'Flexible hours'],
            skills: ['React', 'TypeScript', 'CSS', 'JavaScript'],
            remote: false,
            urgentHiring: false
          },
          {
            id: 'job2',
            title: 'DevOps Engineer',
            department: 'Engineering',
            location: 'Remote',
            type: 'Remote',
            salaryMin: 110000,
            salaryMax: 150000,
            description: 'Maintain and improve our cloud infrastructure.',
            postedDate: '2024-01-10',
            requirements: ['AWS experience', 'Kubernetes', 'CI/CD pipelines'],
            experienceLevel: 'Mid Level',
            benefits: ['Remote work', 'Health insurance', 'Training budget'],
            skills: ['AWS', 'Kubernetes', 'Docker', 'Terraform'],
            remote: true,
            urgentHiring: true
          }
        ],
        reviews: [
          {
            title: 'Good work environment',
            rating: 4.0,
            role: 'Software Engineer',
            date: '2024-01-10',
            currentEmployee: true,
            pros: 'Great team, interesting projects, good benefits',
            cons: 'Could have better career growth opportunities'
          }
        ],
        competitors: [
          { name: 'Company A', rating: 4.0, avgSalary: 100000, openJobs: 150 },
          { name: 'Company B', rating: 4.2, avgSalary: 110000, openJobs: 200 },
          { name: 'This Company', rating: 4.1, avgSalary: 105000, openJobs: 175 }
        ],
        recentNews: [
          {
            title: 'Company announces expansion plans',
            summary: 'Major growth initiative in new markets.',
            url: 'https://example.com/news',
            publishedAt: '2024-01-10T12:00:00Z',
            source: 'Tech News'
          }
        ],
        glassdoorData: {
          overallRating: 4.1,
          cultureRating: 4.0,
          diversityRating: 3.9,
          workLifeBalanceRating: 4.2,
          seniorManagementRating: 3.8,
          compensationRating: 4.0,
          careerOpportunitiesRating: 3.9,
          recommendToFriendPct: 75,
          ceoApprovalPct: 80,
          businessOutlook: 'positive',
          totalReviews: 1234,
          salaryTransparency: 3.8
        },
        levelsData: {
          averageTotalCompensation: 115000,
          medianTotalCompensation: 105000,
          salaryByLevel: [
            { level: 'E3', title: 'Engineer', baseSalary: 90000, totalCompensation: 105000, stockValue: 10000, bonus: 5000 },
            { level: 'E4', title: 'Senior Engineer', baseSalary: 110000, totalCompensation: 130000, stockValue: 15000, bonus: 5000 }
          ],
          compensationTrends: [
            { year: 2022, averageCompensation: 105000 },
            { year: 2023, averageCompensation: 115000 }
          ]
        },
        avgSalary: 115000,
        growthRate: 8.5,
        employeeCount: 5000,
        hasLayoffs: false,
        metrics: {
          workLifeBalance: 4.2,
          careerOpportunities: 3.9,
          compensationBenefits: 4.0,
          seniorManagement: 3.8,
          cultureValues: 4.0
        },
        jobMarketData: {
          totalOpenings: 175,
          hiringTrend: 'stable',
          averageTimeToHire: 35,
          mostInDemandSkills: ['JavaScript', 'React', 'Node.js', 'AWS'],
          departmentGrowth: [
            { department: 'Engineering', growthRate: 10, openings: 120 },
            { department: 'Product', growthRate: 5, openings: 35 },
            { department: 'Marketing', growthRate: 8, openings: 20 }
          ]
        }
      }
    };
    
    return mockCompanies[companyId] || mockCompanies['default'];
  }
}

export const companyDataService = new CompanyDataService();
export default companyDataService;